import { IlamBilgisiEntity } from "@/entity/IlamBilgisiEntity";
import { IlamTipi } from "@/enum/IlamTipi";

/**
 * DEV-2916 — Alacaklar ekranındaki ilam bilgilerini takip ana kartına taşırken
 * kullanılan dönüşüm.
 *
 * Kaynak, Alacaklar tarafının da beslendiği `alacaklar` kayıtlarıdır
 * (`/api/v1/takip/{id}/alacak-detay`); burada yalnızca özet alanlar seçilip
 * ilam tipine göre doğru kolondan okunur. Ekstra bir API'ye ihtiyaç yoktur.
 */

/**
 * İlamı veren makamın adı ilam tipine göre farklı kolonlarda tutuluyor:
 * mahkeme ilamında ilişkili mahkeme kaydında, noter senedinde noter kaydında,
 * hakem/arabulucu/diğer tiplerinde ise serbest metin alanında.
 */
export function ilamiVerenMakam(alacak: any): string | null {
    if (!alacak) return null;

    const mahkemeAdi = alacak.mahkeme?.isim || alacak.ozel_mahkeme_isim || null;

    if (alacak.ilam_tipi === IlamTipi.noter) {
        return alacak.noter?.isim || alacak.ilami_veren_kurum || null;
    }

    if (alacak.ilam_tipi === IlamTipi.mahkeme) {
        return mahkemeAdi;
    }

    // hakem_karari / arabulucu / diger -> serbest metin alanı
    return alacak.ilami_veren_kurum || mahkemeAdi;
}

/**
 * Kartta gösterilebilecek tek bir alanı bile olmayan kayıt listeye alınmaz.
 * Böylece "ilam bilgisi yoksa alan hiç gözükmesin" kuralı kayıt bazında işler;
 * sırf ilam tipi seçilmiş ama içi boş bir alacak da elenir.
 */
function gosterilebilirMi(ilam: IlamBilgisiEntity): boolean {
    return [
        ilam.ilami_veren,
        ilam.dosya_turu,
        ilam.esas_no,
        ilam.karar_no,
        ilam.karar_tarihi,
        ilam.yevmiye_no,
    ].some((deger) => deger !== null && deger !== undefined && deger !== "");
}

/** Alacak kimliği ve alacak belge türü aynı ilamı çoğaltmaz. */
function ilamAnahtari(ilam: IlamBilgisiEntity): string | null {
    const alan = (deger: unknown): string => deger == null ? "" : String(deger).trim();
    // Eksik alanlarla farklı kararları yanlışlıkla birleştirmeyelim.
    if (!alan(ilam.ilami_veren) || !alan(ilam.karar_tarihi)
        || ![ilam.esas_no, ilam.karar_no, ilam.yevmiye_no].some(deger => alan(deger))) return null;
    return JSON.stringify([
        ilam.ilam_tipi, ilam.ilami_veren, ilam.dosya_turu,
        ilam.esas_no, ilam.karar_no, ilam.karar_tarihi, ilam.yevmiye_no,
    ].map(alan));
}

/**
 * Takip taleplerini düzleştirip ilam özetini üretir.
 *
 * Takip birden fazla talep içerebilir (ikili takip); hepsinin alacakları
 * tek listede toplanır, böylece ikinci talebin ilamı da karta düşer.
 */
export function taleplerdenIlamBilgileri(talepler: any[]): IlamBilgisiEntity[] {
    if (!Array.isArray(talepler)) return [];

    const alacaklar = talepler.reduce((hepsi: any[], talep: any) => {
        return hepsi.concat(Array.isArray(talep?.alacaklar) ? talep.alacaklar : []);
    }, []);

    return alacaklardanIlamBilgileri(alacaklar);
}

/**
 * Alacak kayıtlarını ana karttaki İlam Bilgileri listesine çevirir.
 * İlam verisi olmayan takiplerde boş dizi döner ve kart hiç render edilmez.
 */
export function alacaklardanIlamBilgileri(alacaklar: any[]): IlamBilgisiEntity[] {
    if (!Array.isArray(alacaklar)) return [];

    const gorulenIlamlar = new Set<string>();
    return alacaklar
        .map((alacak): IlamBilgisiEntity => {
            const ilam = new IlamBilgisiEntity();
            ilam.alacak_id = alacak.id;
            ilam.alacak_belge_turu = alacak.alacak_belge_turu?.isim ?? null;
            ilam.ilam_tipi = alacak.ilam_tipi ?? null;
            ilam.ilami_veren = ilamiVerenMakam(alacak);
            // Kartta "İlam Türü" etiketiyle gösterilir; Alacaklar formundaki
            // "Dosya Türü" alanının aynısıdır.
            ilam.dosya_turu = alacak.dosya_turu?.isim ?? null;
            ilam.esas_no = alacak.esas_no ?? null;
            ilam.karar_no = alacak.karar_no ?? null;
            ilam.karar_tarihi = alacak.karar_tarihi ?? null;
            ilam.yevmiye_no = alacak.yevmiye_no ?? null;
            return ilam;
        })
        .filter(gosterilebilirMi)
        .sort((a, b) => Number(a.alacak_id) - Number(b.alacak_id))
        .filter(ilam => {
            const anahtar = ilamAnahtari(ilam);
            if (anahtar === null) return true;
            if (gorulenIlamlar.has(anahtar)) return false;
            gorulenIlamlar.add(anahtar);
            return true;
        });
}
